import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProviderKeys from '../components/prompts/ProviderKeys';
import { firstSentence, formatLastUsed } from '../components/prompts/promptModel';
import './AiPrompts.css';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const AiPrompts = () => {
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    (async () => {
      const response = await fetchWithAuth('/api/admin/prompts');
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Request failed (${response.status})`);
      }
      if (isCurrent) {
        setPrompts(data);
      }
    })().catch((loadError) => {
      if (isCurrent) {
        setError(loadError.message);
      }
    });
    return () => {
      isCurrent = false;
    };
    // fetchWithAuth is recreated on every AuthContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (prompts || []).filter((prompt) => (
      !needle
      || prompt.name.toLowerCase().includes(needle)
      || (prompt.note || '').toLowerCase().includes(needle)
      || prompt.model.toLowerCase().includes(needle)
    ));
  }, [prompts, query]);

  return (
    <div className="prompt-page">
      <header className="pe-header">
        <div className="pe-title">
          <h1 className="pe-list-title">AI Prompts</h1>
          {prompts && <span className="pe-count">{prompts.length}</span>}
        </div>
        <div className="pe-actions">
          <input
            className="pe-input is-search"
            type="search"
            aria-label="Search prompts"
            placeholder="Search prompts"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Link className="pe-button is-primary" to="/ai-prompts/new">New prompt</Link>
        </div>
      </header>

      {error && <div className="pe-alert" role="alert">{error}</div>}
      {prompts === null && !error && <p className="pe-muted pe-loading">Loading prompts…</p>}

      <div className="pe-list">
        <ProviderKeys />
      </div>

      {prompts !== null && (
        <div className="pe-list is-prompts">
          <h2>Prompts</h2>
          <p className="pe-muted">The API serves these to the apps. Each build pins the version number it was tested with. Requests and last use count only builds that go through this server, not older builds that call OpenAI directly.</p>
          <div className="pe-table-wrap">
            <table className="pe-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Default</th>
                  <th className="pe-optional">Latest</th>
                  <th>Model</th>
                  <th>Requests, 30 days</th>
                  <th>Last used</th>
                  <th className="pe-optional">Versions</th>
                  <th className="pe-optional">Updated</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((prompt) => (
                  <tr key={prompt.name} onClick={() => navigate(`/ai-prompts/${prompt.name}`)}>
                    <td>
                      <Link className="pe-table-name" to={`/ai-prompts/${prompt.name}`} onClick={(event) => event.stopPropagation()}>{prompt.name}</Link>
                      {prompt.note && <span className="pe-table-note">{firstSentence(prompt.note)}</span>}
                    </td>
                    <td>{prompt.default_version ? <span className="pe-badge">v{prompt.default_version}</span> : '—'}</td>
                    <td className="pe-optional">v{prompt.latest_version}</td>
                    <td className="pe-table-model">{prompt.model}</td>
                    <td>{prompt.requests_30_days.toLocaleString()}</td>
                    <td>{formatLastUsed(prompt.last_requested_at)}</td>
                    <td className="pe-optional">{prompt.versions}</td>
                    <td className="pe-optional">{formatDate(prompt.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shown.length === 0 && (
              <p className="pe-muted pe-table-empty">{prompts.length === 0 ? 'No prompts yet.' : 'No prompts match your search.'}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPrompts;
